import React, { useState, useEffect } from "react";
import { loadTossPayments } from "@tosspayments/tosspayments-sdk";
import { Check, Lock } from "lucide-react";

// ──────────────────────────────────────────────
// PricingPage — 구독 요금제 + 토스 결제위젯 (docuai/docuai2/loginhs 공유)
//   세번분류 무료 / 문서인식 하루 10p 무료 → 유료 무제한.
//   토스 빌링: requestBillingAuth → /?page=payment-success 콜백.
// ──────────────────────────────────────────────

const INK = "#16151a";
const DARK = "#1b1a20";
const LINE = "#ecedf2";
const MUTED = "#6b7280";

const token = () => localStorage.getItem("token");
const won = (n) => (n || 0).toLocaleString("ko-KR") + "원";
const rand = () =>
  "ck_" + Math.random().toString(36).slice(2) + Date.now().toString(36);

// 같은 티어의 월 요금 (연간가 절약폭 환산용)
const MONTHLY_BASE = { Starter: 11000, Pro: 33000, Premium: 55000 };

// 티어별 차별 기능 (가치 사다리 — 페이지 수가 아니라 '무엇을 할 수 있나')
const TIER_FEATURES = {
  Starter: [
    "인보이스 → 세번 10자리 자동 분류",
    "분류 근거(사례·법령·세율) 함께",
    "처리 이력·클라우드 보관",
  ],
  Pro: [
    "Starter 전체 기능",
    "여러 파일 한 번에 (배치 처리)",
    "엑셀 내보내기",
    "자주 쓰는 양식 템플릿 저장",
  ],
  Premium: [
    "Pro 전체 기능",
    "대량 처리 (팀·고볼륨)",
    "API 제공 (사내 시스템 연동)",
    "우선 지원",
  ],
};

export default function PricingPage({ onClose, onLoginRequired }) {
  const [allPlans, setAllPlans] = useState(null);
  const [clientKey, setClientKey] = useState(null);
  const [license, setLicense] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cycle, setCycle] = useState("MONTHLY"); // MONTHLY | YEARLY
  const isMobile = window.innerWidth < 768;

  useEffect(() => {
    fetch("/api/payment/plans")
      .then((r) => r.json())
      .then((d) => {
        setClientKey(d.clientKey);
        setAllPlans(
          (d.plans || []).filter((p) => p.type === "INDIVIDUAL" && p.price > 0),
        );
      })
      .catch(() => setAllPlans([]));
    if (token())
      fetch("/api/payment/license", {
        headers: { Authorization: `Bearer ${token()}` },
      })
        .then((r) => r.json())
        .then((d) => setLicense(d.license))
        .catch(() => {});
  }, []);

  // 로그인 후 복귀 — 저장된 플랜으로 결제창 자동 재개
  useEffect(() => {
    if (!allPlans || !clientKey || !token()) return;
    const seq = sessionStorage.getItem("pay_intent_configSeq");
    if (!seq) return;
    const plan = allPlans.find((p) => String(p.configSeq) === seq);
    sessionStorage.removeItem("pay_intent_configSeq");
    if (plan) setTimeout(() => subscribe(plan), 300);
  }, [allPlans, clientKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // 현재 결제주기로 필터 (월/연 토글)
  const plans = (allPlans || []).filter((p) => p.cycle === cycle);
  const hasYearly = (allPlans || []).some((p) => p.cycle === "YEARLY");

  const subscribe = async (plan) => {
    if (!token()) {
      // 로그인 후 결제로 이어가도록 의도 저장 → 로그인 화면으로
      sessionStorage.setItem("pay_after_login", "pricing");
      sessionStorage.setItem("pay_intent_configSeq", String(plan.configSeq));
      if (onLoginRequired) onLoginRequired();
      return;
    }
    if (!clientKey) {
      alert("결제 모듈을 불러오지 못했습니다.");
      return;
    }
    setBusy(true);
    try {
      const customerKey = rand();
      sessionStorage.setItem("pay_customerKey", customerKey);
      sessionStorage.setItem("pay_configSeq", String(plan.configSeq));
      const toss = await loadTossPayments(clientKey);
      const payment = toss.payment({ customerKey });
      await payment.requestBillingAuth({
        method: "CARD",
        successUrl: window.location.origin + "/?page=payment-success",
        failUrl: window.location.origin + "/?page=payment-fail",
        customerName: localStorage.getItem("userId") || "고객",
      });
    } catch (e) {
      setBusy(false);
      if (!/취소|cancel/i.test(e.message || ""))
        alert("결제 요청 실패: " + (e.message || ""));
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#fafafa",
        fontFamily:
          "'Malgun Gothic', '맑은 고딕', 'Pretendard', -apple-system, 'Segoe UI', sans-serif",
        padding: isMobile ? "32px 16px" : "56px 24px",
        overflow: "auto",
      }}
    >
      <div style={{ maxWidth: 940, margin: "0 auto" }}>
        {onClose && (
          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              color: MUTED,
              fontSize: 13.5,
              cursor: "pointer",
              marginBottom: 16,
            }}
          >
            ← 돌아가기
          </button>
        )}
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div
            style={{
              fontSize: 12.5,
              fontWeight: 700,
              color: "#9ca3af",
              letterSpacing: 2.5,
            }}
          >
            PRICING
          </div>
          <h1
            style={{
              fontSize: isMobile ? 26 : 32,
              fontWeight: 800,
              color: INK,
              margin: "10px 0 8px",
              letterSpacing: -0.5,
            }}
          >
            인보이스 한 장이면, 세번까지 끝.
          </h1>
          <p
            style={{ fontSize: 14.5, color: MUTED, margin: 0, lineHeight: 1.6 }}
          >
            품명으로 세번 찾기는 누구나 무료입니다. 서류를 올리면 품목을 읽어
            10자리 세번까지 자동으로 — 한 건당 5분이 10초로 줄어듭니다.
            <br />
            먼저 무료로 정확도를 확인하고, 업무에 통째로 투입할 때 구독하세요.
          </p>
        </div>

        {license && (
          <div
            style={{
              textAlign: "center",
              marginBottom: 24,
              padding: "12px 16px",
              background: "#e6f4ea",
              border: "1px solid #b7e1c4",
              borderRadius: 12,
              fontSize: 14,
              color: "#137333",
            }}
          >
            현재 <b>{license.tier}</b> 구독 중 — {license.endDate?.slice(0, 4)}.
            {license.endDate?.slice(4, 6)}.{license.endDate?.slice(6, 8)}까지
          </div>
        )}

        {/* 월/연 토글 */}
        {hasYearly && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: 26,
            }}
          >
            <div
              style={{
                display: "inline-flex",
                background: "#f1f3f8",
                borderRadius: 999,
                padding: 4,
                gap: 2,
              }}
            >
              {[
                ["MONTHLY", "월간"],
                ["YEARLY", "연간"],
              ].map(([c, label]) => (
                <button
                  key={c}
                  onClick={() => setCycle(c)}
                  style={{
                    border: "none",
                    borderRadius: 999,
                    padding: "7px 18px",
                    fontSize: 13.5,
                    fontWeight: 700,
                    cursor: "pointer",
                    background: cycle === c ? "#fff" : "transparent",
                    color: cycle === c ? INK : MUTED,
                    boxShadow:
                      cycle === c ? "0 1px 3px rgba(20,21,26,0.12)" : "none",
                  }}
                >
                  {label}
                  {c === "YEARLY" && (
                    <span style={{ color: ACCENT_TX, marginLeft: 5 }}>
                      2개월 무료
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 무료 안내 카드 */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr"
              : `repeat(${(plans?.length || 0) + 1}, 1fr)`,
            gap: 16,
            alignItems: "stretch",
          }}
        >
          <PlanCard
            free
            name="무료"
            price={0}
            usage="문서인식 하루 10건 체험"
            isMobile={isMobile}
            features={[
              "세번 분류 무제한",
              "분류 근거(사례·법령·세율)",
              "한 건씩 인보이스 자동분류 체험",
            ]}
            onFree={() => {
              // 로그인 상태면 앱으로, 아니면 로그인/가입으로 — 죽은 회색 박스였던 것 수정
              if (token()) window.location.href = "/";
              else if (onLoginRequired) onLoginRequired();
            }}
          />
          {(plans || []).map((p) => (
            <PlanCard
              key={p.configSeq}
              name={p.name}
              price={p.price}
              cycle={cycle}
              usage={`${cycle === "YEARLY" ? "연" : "월"} 문서인식 ${p.maxPages.toLocaleString()}페이지`}
              featured={/pro/i.test(p.name)}
              features={TIER_FEATURES[p.name] || []}
              onSubscribe={() => subscribe(p)}
              busy={busy}
              isMobile={isMobile}
            />
          ))}
        </div>
        {!allPlans && (
          <div style={{ textAlign: "center", color: MUTED, marginTop: 30 }}>
            요금제 불러오는 중…
          </div>
        )}
        <p
          style={{
            textAlign: "center",
            fontSize: 12,
            color: "#9ca3af",
            marginTop: 22,
            lineHeight: 1.6,
          }}
        >
          모든 요금제는 세번 분류·사례·법령·세율 조회 무제한. 표시 한도는
          문서인식 처리량 기준이며 부가세 포함입니다.
        </p>
      </div>
    </div>
  );
}

// 팔레트 — 사이트 공통 CTA(#111827, 랜딩·포털과 동일)로 통일. 파스텔 블루 CTA 는
// 비활성처럼 읽혀 폐기(구 GREEN=#BBDEFB). 블루는 체크·절약 배지 등 보조 포인트로만.
const CTA_DARK = "#111827";
const BLUE_SOFT = "#e3f2fd";
const BLUE_DEEP = "#0d47a1";
const ACCENT_TX = "#1565c0"; // 체크·연간 절약 등 보조 텍스트용 블루

function PlanCard({
  free,
  name,
  price,
  usage,
  cycle = "MONTHLY",
  features,
  featured,
  onSubscribe,
  onFree,
  busy,
  isMobile,
}) {
  const yearly = cycle === "YEARLY";
  // 연간은 월 환산가를 크게, 연 총액을 보조로 — 비교를 쉽게
  const perMonth = yearly ? Math.round(price / 12 / 100) * 100 : price;
  const monthlyEquivBase = name && MONTHLY_BASE[name]; // 같은 티어 월요금
  const saveMonths =
    yearly && monthlyEquivBase
      ? Math.round((monthlyEquivBase * 12 - price) / monthlyEquivBase)
      : 0;
  const btnBase = {
    width: "100%",
    padding: "12px 0",
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 700,
    fontFamily: "inherit",
    cursor: busy ? "default" : "pointer",
  };
  return (
    <div
      style={{
        position: "relative",
        border: featured ? `1.5px solid ${CTA_DARK}` : `1px solid ${LINE}`,
        borderRadius: 16,
        padding: "28px 22px 24px",
        background: "#fff",
        display: "flex",
        flexDirection: "column",
        wordBreak: "keep-all", // 한글 단어 중간 줄바꿈 방지 — "배치 처/리" 류 개행 금지
        boxShadow: featured
          ? "0 16px 40px -16px rgba(15,23,42,0.25)"
          : "0 1px 3px rgba(15,23,42,0.05)",
        transform: featured && !isMobile ? "translateY(-8px)" : "none",
      }}
    >
      {featured && (
        <span
          style={{
            position: "absolute",
            top: -12,
            left: "50%",
            transform: "translateX(-50%)",
            fontSize: 11.5,
            fontWeight: 700,
            color: "#fff",
            background: CTA_DARK,
            padding: "4px 14px",
            borderRadius: 999,
            whiteSpace: "nowrap",
            letterSpacing: 0.3,
          }}
        >
          가장 인기
        </span>
      )}
      <span
        style={{ fontSize: 15.5, fontWeight: 700, color: INK, letterSpacing: -0.2 }}
      >
        {name}
      </span>
      <div
        style={{
          margin: "14px 0 4px",
          display: "flex",
          alignItems: "baseline",
          gap: 5,
        }}
      >
        <span
          style={{
            fontSize: 32,
            fontWeight: 800,
            color: INK,
            letterSpacing: -1,
            lineHeight: 1,
          }}
        >
          {price === 0 ? "0원" : won(perMonth)}
        </span>
        <span style={{ fontSize: 13, color: "#9ca3af", fontWeight: 500 }}>
          / 월
        </span>
      </div>
      <span style={{ fontSize: 12, color: "#9ca3af", fontWeight: 500 }}>
        {price > 0
          ? yearly
            ? `연 ${won(price)} 일시불 · 부가세 포함`
            : "부가세 포함"
          : "신용카드 없이 바로"}
      </span>
      {saveMonths > 0 && (
        <span
          style={{
            marginTop: 8,
            alignSelf: "flex-start",
            fontSize: 11.5,
            fontWeight: 700,
            color: BLUE_DEEP,
            background: BLUE_SOFT,
            padding: "3px 9px",
            borderRadius: 999,
          }}
        >
          연간 결제 {saveMonths}개월분 절약
        </span>
      )}
      {usage && (
        <span
          style={{
            marginTop: 14,
            paddingTop: 14,
            borderTop: `1px solid ${LINE}`,
            fontSize: 12.5,
            color: MUTED,
            fontWeight: 600,
          }}
        >
          {usage}
        </span>
      )}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 11,
          margin: usage ? "12px 0 24px" : "20px 0 24px",
          flex: 1,
        }}
      >
        {features.map((f, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "flex-start", // 두 줄 피처에서 체크가 세로 중앙에 뜨지 않도록
              gap: 8,
              fontSize: 13.5,
              lineHeight: 1.5,
              color: "#374151",
            }}
          >
            <Check
              size={15}
              color={ACCENT_TX}
              strokeWidth={2.6}
              style={{ flexShrink: 0, marginTop: 2.5 }}
            />
            <span>{f}</span>
          </div>
        ))}
      </div>
      {free ? (
        <button
          onClick={onFree}
          style={{
            ...btnBase,
            border: "1.5px solid #d1d5db",
            background: "#fff",
            color: INK,
            cursor: "pointer",
          }}
        >
          {token() ? "앱에서 바로 사용 →" : "무료로 시작"}
        </button>
      ) : featured ? (
        <button
          onClick={onSubscribe}
          disabled={busy}
          style={{
            ...btnBase,
            border: "none",
            background: CTA_DARK,
            color: "#fff",
            boxShadow: "0 4px 14px -4px rgba(15,23,42,0.4)",
          }}
        >
          {busy ? "처리 중…" : "이 요금제로 시작"}
        </button>
      ) : (
        <button
          onClick={onSubscribe}
          disabled={busy}
          style={{
            ...btnBase,
            border: `1.5px solid ${CTA_DARK}`,
            background: "#fff",
            color: CTA_DARK,
          }}
        >
          {busy ? "처리 중…" : "이 요금제로 시작"}
        </button>
      )}
    </div>
  );
}

// ─── 결제 성공/실패 콜백 페이지 ───────────────────────────
export function PaymentResultPage({ kind, onDone }) {
  const [status, setStatus] = useState(
    kind === "success" ? "결제를 확인하는 중…" : "결제가 취소되었습니다.",
  );
  const [done, setDone] = useState(kind !== "success");

  useEffect(() => {
    if (kind !== "success") return;
    const p = new URLSearchParams(window.location.search);
    const authKey = p.get("authKey");
    const customerKey =
      p.get("customerKey") || sessionStorage.getItem("pay_customerKey");
    const configSeq = sessionStorage.getItem("pay_configSeq");
    const tk = localStorage.getItem("token");
    if (!authKey || !customerKey || !configSeq || !tk) {
      setStatus("결제 정보가 올바르지 않습니다.");
      setDone(true);
      return;
    }
    (async () => {
      try {
        const ib = await fetch("/api/payment/issue-billing", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tk}`,
          },
          body: JSON.stringify({ authKey, customerKey }),
        });
        const ibd = await ib.json();
        if (!ib.ok) throw new Error(ibd.error || "빌링키 발급 실패");
        const cf = await fetch("/api/payment/confirm", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tk}`,
          },
          body: JSON.stringify({
            customerKey,
            billingKey: ibd.billingKey,
            configSeq: parseInt(configSeq, 10),
          }),
        });
        const cfd = await cf.json();
        if (!cf.ok) throw new Error(cfd.error || "결제 승인 실패");
        sessionStorage.removeItem("pay_customerKey");
        sessionStorage.removeItem("pay_configSeq");
        setStatus(
          "구독이 완료되었습니다! 이제 문서인식 기능을 사용할 수 있어요.",
        );
      } catch (e) {
        setStatus("결제 처리 실패: " + (e.message || ""));
      } finally {
        setDone(true);
      }
    })();
  }, [kind]);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#fafafa",
        fontFamily: "'Malgun Gothic', '맑은 고딕', 'Pretendard', -apple-system, sans-serif",
        padding: 24,
      }}
    >
      <div
        style={{
          maxWidth: 420,
          width: "100%",
          background: "#fff",
          border: `1px solid ${LINE}`,
          borderRadius: 16,
          padding: "36px 28px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            margin: "0 auto 16px",
            borderRadius: "50%",
            background: kind === "success" ? "#e6f4ea" : "#fdeaea",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {kind === "success" ? (
            <Check size={24} color="#137333" />
          ) : (
            <Lock size={22} color="#dc2626" />
          )}
        </div>
        <p
          style={{
            fontSize: 15,
            color: INK,
            lineHeight: 1.6,
            margin: "0 0 20px",
          }}
        >
          {status}
        </p>
        {done && (
          <button
            onClick={() => {
              window.history.pushState({}, "", "/");
              window.dispatchEvent(new PopStateEvent("popstate"));
              onDone && onDone();
            }}
            style={{
              padding: "11px 22px",
              border: "none",
              borderRadius: 10,
              background: DARK,
              color: "#fff",
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            돌아가기
          </button>
        )}
      </div>
    </div>
  );
}
